import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Loader2, Send, KeyRound, CheckCircle2 } from "lucide-react";
import { AdminCard, AdminShell, PasswordInput, adminInputCls, adminLabelCls, formatApiError, passwordStrength } from "@/components/admin-ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async (fn) => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const sendCode = (e) => {
    e.preventDefault();
    run(async () => {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep(2);
    });
  };

  const verifyCode = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await axios.post(`${API}/auth/verify-reset-code`, { email, code });
      setResetToken(data.reset_token);
      setStep(3);
    });
  };

  const resetPassword = (e) => {
    e.preventDefault();
    if (pw !== pw2) {
      setError("Passwords do not match.");
      return;
    }
    if (passwordStrength(pw) < 5) {
      setError("Password must be at least 12 characters with uppercase, lowercase, a number and a symbol.");
      return;
    }
    run(async () => {
      await axios.post(`${API}/auth/reset-password`, { reset_token: resetToken, new_password: pw });
      setStep(4);
    });
  };

  return (
    <AdminShell>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md" data-testid="forgot-password-page">
        <AdminCard
          title={step === 4 ? "All Done" : "Forgot your password?"}
          subtitle={step === 1 ? "Enter the email address associated with your administrator account and we'll send you a secure verification code." : step === 2 ? "Enter Verification Code — check the registered administrator email." : step === 3 ? "Create a new strong password." : null}
        >
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.form key="s1" onSubmit={sendCode} className="space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div>
                  <label htmlFor="fp-email-input" className={adminLabelCls}>Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input id="fp-email-input" data-testid="fp-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Administrator email" className={`${adminInputCls} pl-10`} />
                  </div>
                </div>
                {error && <p data-testid="fp-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{error}</p>}
                <button type="submit" data-testid="fp-send-button" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-4 font-accent text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Verification Code
                </button>
              </motion.form>
            )}
            {step === 2 && (
              <motion.form key="s2" onSubmit={verifyCode} className="space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div>
                  <label htmlFor="fp-code-input" className={adminLabelCls}>Verification Code</label>
                  <div className="relative">
                    <KeyRound className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input id="fp-code-input" data-testid="fp-code-input" required inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className={`${adminInputCls} pl-10 text-center text-xl tracking-[0.5em]`} />
                  </div>
                  <p className="mt-2 text-xs text-muted">The code expires in 10 minutes and can be used only once.</p>
                </div>
                {error && <p data-testid="fp-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{error}</p>}
                <button type="submit" data-testid="fp-verify-button" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-4 font-accent text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Verify Code
                </button>
              </motion.form>
            )}
            {step === 3 && (
              <motion.form key="s3" onSubmit={resetPassword} className="space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <PasswordInput label="New Password" testid="fp-new-password" value={pw} onChange={(e) => setPw(e.target.value)} showStrength />
                <PasswordInput label="Confirm New Password" testid="fp-confirm-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
                {error && <p data-testid="fp-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-600">{error}</p>}
                <button type="submit" data-testid="fp-reset-button" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-4 font-accent text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Reset Password
                </button>
              </motion.form>
            )}
            {step === 4 && (
              <motion.div key="s4" className="text-center" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="fp-success">
                <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.5} />
                <p className="mt-5 text-base leading-relaxed text-muted">
                  Password reset successfully. You can now log in with your new password.
                </p>
                <Link to="/admin" data-testid="fp-back-login" className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 font-accent text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold">
                  Return to Admin Login
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
          {step < 4 && (
            <p className="mt-7 text-center text-sm">
              <Link to="/admin" className="text-muted transition-colors hover:text-navy">← Return to Admin Login</Link>
            </p>
          )}
        </AdminCard>
      </motion.div>
    </AdminShell>
  );
}
